import { i18n } from "./i18n";

function normalizePattern(pattern: string): string {
  // Keycloak が返す \x{...} を JS 用の \u{...} に変換
  return pattern.replace(/\\x\{([0-9A-Fa-f]+)\}/g, "\\u{$1}");
}

function tWithParams(key: string, params: any[]) {
  let msg = i18n.t(key);

  params.forEach((p, i) => {
    const placeholder = new RegExp(`\\{${i}\\}`, "g");
    msg = msg.replace(placeholder, p);
  });

  return msg;
}

// -----------------------------
// アカウントID・誕生日・住所以外のチェック
// -----------------------------
export const validateValue = (
  attr: any,
  value: string,
): string | null => {
  const v = attr.validators || {};
  const skipValidationNames = ["account_id", "birthday", "zip_code", "prefectures", "municipalities", "street", "building"];
  if (skipValidationNames.includes(attr.name)) {
    return null;
  }

  // -----------------------------
  // 電話番号
  // -----------------------------
  if (attr.name === "phone_number") {
    if (value.includes("-")) {
      return "screen-valid.error-pattern-phoneNumber_hyphen";
    }
  }

  // -----------------------------
  // 文字数チェック
  // -----------------------------
  if (v.length) {
    const { min, max } = v.length;
    if (value.length === 0) {
        return "screen-valid.error-user-attribute-required";
    }

    if (min && value.length < min) {
      return tWithParams("screen-valid.error-invalid-length", ["", min, max]);
    }
    if (max && value.length > max) {
      return tWithParams("screen-valid.error-invalid-length", ["", min, max]);
    }
  }

  // -----------------------------
  // パターンチェック
  // -----------------------------
  if (v.pattern) {
    try {
      const normalized = normalizePattern(v.pattern.pattern);
      const regex = new RegExp(normalized, "u");

      if (!regex.test(value)) {
        return tWithParams(v.pattern["error-message"], [i18n.t(attr.name)]) || "error-pattern-not_exist";
      }
    } catch (e) {
      console.warn("Invalid regex from Keycloak:", v.pattern.pattern, e);
      return "error-pattern-invalid-format";
    }
  }

  // -----------------------------
  // 性別
  // -----------------------------
  if (attr.name === "sex") {
    const allowed = ["0", "1", "9"];
    const isValid = value.length === 1 && allowed.includes(value);
    if (!isValid) {
      return tWithParams("error-pattern-sex", [i18n.t(attr.name)]);
    }
    return null;
  }

  return null;
}

// -----------------------------
// 生年月日バリデーション
// -----------------------------
export const validateBirthday = (raw: string | undefined) => {
  if (!raw) return "required";

  if (raw.length !== 8) return "invalid";

  const y = Number(raw.substring(0, 4));
  const m = Number(raw.substring(4, 6));
  const d = Number(raw.substring(6, 8));

  const date = new Date(y, m - 1, d);

  const invalid =
    date.getFullYear() !== y ||
    date.getMonth() + 1 !== m ||
    date.getDate() !== d;

  return invalid ? "invalid" : null;
};

// -----------------------------
// 住所バリデーション
// -----------------------------
export const validateAddressGroup = (
  attributes: Record<string, any>,
  autoFillMode: boolean
): Record<string, string | null> => {

  const fields = ["zip_code", "prefectures", "municipalities", "street", "building"];
  const requiredFields = ["zip_code", "prefectures", "municipalities", "street"];

  const attrs = attributes ?? {};
  const safe = (v: any) => (v == null ? "" : v);

  const values = fields.map(f => safe(attrs[f]?.[0]));
  const allEmpty = values.every(v => v === "");

  const errors: Record<string, string | null> = {
    zip_code: null,
    prefectures: null,
    municipalities: null,
    street: null,
    building: null,
  };

  // ----------------------------------------
  // 自動入力モード中はバリデーション停止
  // ----------------------------------------
  if (autoFillMode) {
    return errors;
  }

  // ----------------------------------------
  // 全部空ならエラーなし
  // ----------------------------------------
  if (allEmpty) {
    return errors;
  }

  // ----------------------------------------
  // 必須4項目チェック
  // ----------------------------------------
  requiredFields.forEach(f => {
    const v = safe(attrs[f]?.[0]);
    if (v === "") {
      errors[f] = "screen-valid.error-user-attribute-required";
    }
  });

  //zip_codeチェック
  const zip = safe(attrs.zip_code?.[0]);
  if (zip !== "") {

    // ハイフン禁止
    if (zip.includes("-")) {
      errors.zip_code = "screen-valid.error-pattern-zipcode_hyphen";
    }

    // 長さチェック（7 桁）
    else if (zip.length !== 7) {
      errors.zip_code = tWithParams(
        "screen-valid.error-invalid-length_notJust",
        ["", "7"]
      );
    }

    // 半角数字のみ
    else if (!/^[0-9]+$/.test(zip)) {
      errors.zip_code = tWithParams("error-pattern-halfWidth-digit", [i18n.t("zip_code")]);
    }
  }

  // zip_code以外: length チェック
  const rules = {
    prefectures: { min: 1, max: 10 },
    municipalities: { min: 1, max: 120 },
    street: { min: 1, max: 60 },
    building: { min: 0, max: 120 },
  } as const;

  type AddressField = keyof typeof rules;

  (Object.keys(rules) as AddressField[]).forEach(f => {
    const v = safe(attrs[f]?.[0]) ?? "";
    const { min, max } = rules[f];

    if (v !== "" && v.length < min) {
      errors[f] = tWithParams("screen-valid.error-invalid-length", ["", min, max]);
    }
    if (v.length > max) {
      errors[f] = tWithParams("screen-valid.error-invalid-length", ["", min, max]);
    }
  });

  return errors;
};